// skipcq: JS-W1028
import { useEffect, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import {
  AWARDED_SPLATS_COLLECTION,
  SPLAT_DEFINITIONS_COLLECTION,
} from '../utils/firestoreCollections';

function parseAwardedDate(value) {
  if (value == null) return null;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value?.__time__) return new Date(value.__time__);
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

/**
 * Latest awarded splat records with resolved splat name and user display name.
 */
function useRecentAwardedSplats(count = 10) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        if (!cancelled) {
          setLoading(true);
          setError(null);
        }

        const awardsRef = collection(db, AWARDED_SPLATS_COLLECTION);
        const awardsQuery = query(
          awardsRef,
          orderBy('awardedDate', 'desc'),
          limit(count),
        );
        const snapshot = await getDocs(awardsQuery);
        if (cancelled) return;

        const awardDocs = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        const splatIds = [
          ...new Set(awardDocs.map((a) => a.splatId).filter(Boolean)),
        ];
        const userIds = [
          ...new Set(awardDocs.map((a) => a.userId).filter(Boolean)),
        ];

        const [splatPairs, userPairs] = await Promise.all([
          Promise.all(
            splatIds.map(async (splatId) => {
              const snap = await getDoc(
                doc(db, SPLAT_DEFINITIONS_COLLECTION, splatId),
              );
              const name = snap.exists() ? (snap.data().name ?? '') : '';
              return [splatId, name.trim() || 'Unknown splat'];
            }),
          ),
          Promise.all(
            userIds.map(async (userId) => {
              const snap = await getDoc(doc(db, 'users', userId));
              if (!snap.exists()) return [userId, 'Unknown user'];
              const userData = snap.data();
              const label =
                userData.displayName ||
                userData.userName ||
                userData.email ||
                'Unknown user';
              return [userId, label];
            }),
          ),
        ]);

        if (cancelled) return;

        const splatMap = Object.fromEntries(splatPairs);
        const userMap = Object.fromEntries(userPairs);

        const nextRows = awardDocs.map((a) => ({
          id: a.id,
          splatName: splatMap[a.splatId] ?? 'Unknown splat',
          userDisplayName: userMap[a.userId] ?? 'Unknown user',
          awardedDate: parseAwardedDate(a.awardedDate),
        }));

        setRows(nextRows);
      } catch {
        if (!cancelled) {
          setError('Failed to load recent splat awards');
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [count]);

  return { rows, loading, error };
}

export default useRecentAwardedSplats;
